import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Checkbox, SelectInput, TextInput, TextareaInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import {
  dokumentiereVersand,
  formatMahnungDate,
  fuegeRechnungenHinzu,
  listOffeneRechnungenFuerMahnung,
  sendeMahnung,
  storniereMahnung,
  VERSANDART_OPTIONS,
  type MahnungDetail,
  type OffeneRechnungFuerMahnung,
} from "@/lib/mahnwesen";
import { toast } from "@/lib/toast";

function joinMails(...parts: Array<string | null | undefined>): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    for (const raw of String(part || "").split(/[;,]/)) {
      const mail = raw.trim();
      if (!mail) continue;
      const key = mail.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(mail);
    }
  }
  return out.join("; ");
}

// ---------------------------------------------------------------------------
// Per E-Mail senden
// ---------------------------------------------------------------------------

export function MahnungSendenDialog({
  open,
  detail,
  onClose,
  onSent,
}: {
  open: boolean;
  detail: MahnungDetail;
  onClose: () => void;
  onSent: () => void | Promise<void>;
}) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [anhaenge, setAnhaenge] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(detail.empfaenger || detail.kunde_email || "");
    setCc(joinMails(detail.empfaenger_cc, detail.team_cc));
    setSubject(
      detail.email_betreff ||
        `${detail.stufe_bezeichnung}${detail.nummer ? ` ${detail.nummer}` : ""}`,
    );
    setBody(detail.email_text || "");
    setAnhaenge(new Set(detail.positionen.map((p) => p.dokument_id).filter(Boolean)));
  }, [open, detail]);

  const erneut = detail.status === "versendet";

  function toggleAnhang(id: number) {
    setAnhaenge((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSend() {
    if (!to.trim()) {
      toast.error("Bitte mindestens eine Empfänger-E-Mail angeben.");
      return;
    }
    if (!subject.trim()) {
      toast.error("Bitte einen Betreff angeben.");
      return;
    }
    setSending(true);
    try {
      await sendeMahnung(detail.id, {
        to,
        cc,
        subject,
        text_body: body,
        rechnungen_anhaengen: anhaenge.size > 0,
        anhang_dokument_ids: [...anhaenge],
      });
      await onSent();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Versand fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${detail.stufe_bezeichnung}${detail.nummer ? ` ${detail.nummer}` : ""} per E-Mail senden`}
      description={
        erneut
          ? "Diese Mahnung wurde bereits versendet. Ein erneuter Versand wird zusätzlich protokolliert."
          : "Empfänger, Betreff und Text können hier noch angepasst werden. Die Mahnung wird als PDF angehängt."
      }
      size="2xl"
      dirty={sending}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-label-tertiary">
            Anhang: {detail.pdf_filename || "Mahnung.pdf"}
            {anhaenge.size ? ` + ${anhaenge.size} ${anhaenge.size === 1 ? "Rechnung" : "Rechnungen"}` : ""}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
              Abbrechen
            </Button>
            <Button type="button" loading={sending} onClick={() => void handleSend()}>
              {erneut ? "Erneut senden" : "Jetzt senden"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="An"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="empfaenger@firma.de"
            hint={
              detail.empfaenger_quelle_label
                ? `Vorbelegt aus: ${detail.empfaenger_quelle_label}. Mehrere Adressen mit Semikolon trennen.`
                : "Mehrere Adressen mit Semikolon trennen."
            }
          />
          <TextInput
            label="CC"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            hint={detail.team_cc ? "Enthält die im Team hinterlegten Kopie-Adressen." : undefined}
          />
        </div>
        <TextInput label="Betreff" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <TextareaInput
          label="Nachricht"
          rows={9}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          hint="Platzhalter wie {{Mahnung.gesamtforderung}} oder {{Ansprechpartner.briefanrede}} werden beim Versand ersetzt. Die Signatur wird automatisch angefügt."
        />

        <fieldset className="space-y-2 rounded-lg border border-separator/70 p-3">
          <legend className="px-1 text-sm font-medium text-label">Rechnungen als PDF mitschicken</legend>
          {detail.positionen.length === 0 ? (
            <p className="text-xs text-label-secondary">Keine Rechnungen in dieser Mahnung.</p>
          ) : (
            detail.positionen.map((pos) => (
              <Checkbox
                key={pos.id}
                label={`Rechnung ${pos.rechnung_nummer || `#${pos.dokument_id}`}`}
                description={`vom ${formatMahnungDate(pos.rechnung_datum)} · offen ${formatCurrency(pos.betrag_offen, "EUR")}`}
                checked={anhaenge.has(pos.dokument_id)}
                onChange={() => toggleAnhang(pos.dokument_id)}
              />
            ))
          )}
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setAnhaenge(new Set(detail.positionen.map((p) => p.dokument_id)))}
            >
              Alle
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setAnhaenge(new Set())}>
              Keine
            </Button>
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Versand außerhalb der App dokumentieren
// ---------------------------------------------------------------------------

export function VersandDokumentierenDialog({
  open,
  detail,
  onClose,
  onDone,
}: {
  open: boolean;
  detail: MahnungDetail;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const initial = detail.versandart && detail.versandart !== "email" ? detail.versandart : "brief";
  const [versandart, setVersandart] = useState(initial);
  const [notiz, setNotiz] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVersandart(initial);
    setNotiz("");
  }, [open, initial]);

  const option = VERSANDART_OPTIONS.find((o) => o.value === versandart);

  async function handleSubmit() {
    setBusy(true);
    try {
      await dokumentiereVersand(detail.id, { versandart, notiz });
      await onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Versand dokumentieren"
      description="Für den Versand außerhalb der App – z. B. per Post oder persönlich. Die Mahnung wird als versendet markiert; die Zahlungsfrist läuft ab heute."
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" loading={busy} onClick={() => void handleSubmit()}>
            Als versendet markieren
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <SelectInput
          label="Versandart"
          value={versandart}
          onChange={(e) => setVersandart(e.target.value)}
          hint={option?.hinweis}
        >
          {VERSANDART_OPTIONS.filter((o) => o.value !== "email").map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectInput>
        <TextareaInput
          label="Notiz (optional)"
          rows={3}
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          placeholder="z. B. Einschreiben-Nr. 123456"
        />
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Stornieren
// ---------------------------------------------------------------------------

export function StornoDialog({
  open,
  mahnungId,
  onClose,
  onDone,
}: {
  open: boolean;
  mahnungId: number;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [grund, setGrund] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setGrund("");
  }, [open]);

  async function handleSubmit() {
    if (!grund.trim()) {
      toast.error("Bitte einen Stornogrund angeben.");
      return;
    }
    setBusy(true);
    try {
      await storniereMahnung(mahnungId, grund.trim());
      await onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Storno fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Mahnung stornieren"
      description="Die Mahnung bleibt mit Storno-Vermerk in der Historie erhalten. Die Rechnung fällt auf die vorherige Mahnstufe zurück."
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" variant="danger" loading={busy} onClick={() => void handleSubmit()}>
            Stornieren
          </Button>
        </div>
      }
    >
      <TextareaInput
        label="Grund"
        rows={3}
        value={grund}
        onChange={(e) => setGrund(e.target.value)}
        placeholder="z. B. Zahlung war bereits eingegangen"
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Weitere Rechnungen anfügen
// ---------------------------------------------------------------------------

export function RechnungHinzufuegenDialog({
  open,
  detail,
  onClose,
  onAdded,
}: {
  open: boolean;
  detail: MahnungDetail;
  onClose: () => void;
  onAdded: (next: MahnungDetail) => void;
}) {
  const [items, setItems] = useState<OffeneRechnungFuerMahnung[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    setSelected(new Set());
    listOffeneRechnungenFuerMahnung(detail.id)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setItems([]);
        setError(err instanceof ApiError ? err.message : "Offene Rechnungen konnten nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, detail.id]);

  const summe = useMemo(
    () => (items || []).filter((i) => selected.has(i.dokument_id)).reduce((s, i) => s + i.summe_offen, 0),
    [items, selected],
  );

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleAdd() {
    if (!selected.size) return;
    setBusy(true);
    try {
      const next = await fuegeRechnungenHinzu(detail.id, [...selected]);
      toast.success(
        selected.size === 1 ? "Rechnung hinzugefügt." : `${selected.size} Rechnungen hinzugefügt.`,
      );
      onAdded(next);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Rechnungen konnten nicht hinzugefügt werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Weitere Rechnungen anfügen"
      description={`Offene Rechnungen von ${detail.kunde_name || "diesem Kunden"}, die noch nicht Teil dieser Mahnung sind.`}
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-label-secondary">
            {selected.size
              ? `${selected.size} ausgewählt · ${formatCurrency(summe, "EUR")} offen`
              : "Rechnungen auswählen"}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Abbrechen
            </Button>
            <Button type="button" loading={busy} disabled={!selected.size} onClick={() => void handleAdd()}>
              Hinzufügen
            </Button>
          </div>
        </div>
      }
    >
      {error ? <Alert variant="error">{error}</Alert> : null}
      {items == null ? (
        <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
          <Spinner /> Offene Rechnungen werden geladen …
        </div>
      ) : items.length === 0 ? (
        <p className="py-4 text-sm text-label-secondary">
          Keine weiteren offenen Rechnungen für diesen Kunden gefunden.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-label-secondary">
              <tr>
                <th className="w-8 py-1.5" />
                <th className="py-1.5 pr-3 font-semibold">Rechnung</th>
                <th className="py-1.5 pr-3 font-semibold">Fällig</th>
                <th className="py-1.5 pr-3 font-semibold">Bisherige Stufe</th>
                <th className="py-1.5 text-right font-semibold">Offen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.dokument_id}
                  className="cursor-pointer border-t border-separator/60 hover:bg-fill-tertiary/30"
                  onClick={() => toggle(item.dokument_id)}
                >
                  <td className="py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(item.dokument_id)}
                      onChange={() => toggle(item.dokument_id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Rechnung ${item.dokument_nummer || item.dokument_id} auswählen`}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-label">{item.dokument_nummer || `#${item.dokument_id}`}</div>
                    <div className="text-xs text-label-secondary">vom {formatMahnungDate(item.belegdatum)}</div>
                  </td>
                  <td className="py-2 pr-3 tabular-nums">
                    {formatMahnungDate(item.faelligkeitsdatum)}
                    {item.tage_ueberfaellig > 0 ? (
                      <div className="text-xs text-status-orange">{item.tage_ueberfaellig} Tage überfällig</div>
                    ) : (
                      <div className="text-xs text-label-tertiary">noch nicht fällig</div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-label-secondary">
                    {item.mahnstufe && item.mahnstufe !== "keine" ? item.mahnstufe : "Noch nicht gemahnt"}
                    {item.pausiert ? " · pausiert" : ""}
                  </td>
                  <td className="py-2 text-right font-medium tabular-nums">
                    {formatCurrency(item.summe_offen, "EUR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
